import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import './RecipesTab.css';
import { apiGet, apiPost, apiDelete } from '../utils/api';

const BACKUP_CATEGORIES = [
  'Vegetarian',
  'Vegan',
  'Pasta',
  'Breakfast',
  'Dessert',
];

const MEAT_CATEGORIES = ['Beef', 'Chicken', 'Goat', 'Lamb', 'Pork', 'Seafood'];
const VEGETARIAN_CATEGORY = 'Vegetarian';
const CATEGORY_FILTERS = [...MEAT_CATEGORIES, VEGETARIAN_CATEGORY];

const RecipesTab = ({ currentUser }) => {
  const location = useLocation();
  const [recipes, setRecipes] = useState([]);
  const [hasSelections, setHasSelections] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [favoriteIdByExternalId, setFavoriteIdByExternalId] = useState(new Map());
  const [manualSearch, setManualSearch] = useState('');

  useEffect(() => {
    const searchTerm = location.state?.searchTerm;
    if (!searchTerm || !currentUser) {
      return;
    }

    let cancelled = false;

    const runDeepSearch = async () => {
      try {
        setLoading(true);
        setError(null);

        const selections = await apiGet('/user-selections', { auth: true });
        const terms = selections.map((s) => s.deal.name);

        if (terms.length === 0) {
          if (!cancelled) {
            setHasSelections(false);
            setRecipes([]);
            setLoading(false);
          }
          return;
        }

        const results = await Promise.allSettled(
          terms.map((term) => apiGet('/recipes', { query: { ingredient: term } }))
        );

        const allRecipes = results
          .filter((r) => r.status === 'fulfilled')
          .flatMap((r) => r.value);

        const deduped = [...new Map(allRecipes.map((r) => [r.externalId, r])).values()];

        if (!cancelled) {
          setHasSelections(true);
          setRecipes(deduped);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
          console.error('Error searching recipes for term:', err);
        }
      }
    };

    runDeepSearch();

    return () => {
      cancelled = true;
    };
  }, [currentUser, location.state?.searchTerm]);

  useEffect(() => {
    if (!currentUser) {
      setFavoriteIdByExternalId(new Map());
      return;
    }

    const fetchFavorites = async () => {
      try {
        const favorites = await apiGet('/recipe-favorites', { auth: true });
        setFavoriteIdByExternalId(
          new Map(
            favorites
              .filter((f) => f.source === 'TheMealDB')
              .map((f) => [f.externalId, f.id]),
          ),
        );
      } catch (err) {
        console.error('Error loading favorites:', err.message);
      }
    };

    fetchFavorites();

    const onSelectionChange = () => {
      setRecipes([]);
      setHasSelections(true);
      setLoading(true);
      setError(null);
    };

    window.addEventListener('deal-selections-changed', onSelectionChange);
    return () => window.removeEventListener('deal-selections-changed', onSelectionChange);
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const fetchRecipes = async () => {
      try {
        setLoading(true);
        setError(null);

        const selections = await apiGet('/user-selections', { auth: true });

        if (selections.length === 0) {
          setHasSelections(false);
          setRecipes([]);
          setLoading(false);
          return;
        }

        setHasSelections(true);

        const terms = [...new Set(selections.map((s) => s.deal.name))];
        const results = await Promise.allSettled(
          terms.map((term) => apiGet('/recipes', { query: { ingredient: term } }))
        );

        const allRecipes = results
          .filter((r) => r.status === 'fulfilled')
          .flatMap((r) => r.value);

        const deduped = [...new Map(allRecipes.map((r) => [r.externalId, r])).values()];

        setRecipes(deduped);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
        console.error('Error fetching recipes:', err);
      }
    };

    fetchRecipes();
  }, [currentUser]);

  const toggleCategory = (category) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const visibleRecipes = useMemo(() => {
    if (selectedCategories.size === 0) return recipes;
    return recipes.filter((recipe) => selectedCategories.has(recipe.category));
  }, [recipes, selectedCategories]);

  const isFavorited = (recipe) => favoriteIdByExternalId.has(recipe.externalId);

  const toggleFavorite = async (recipe) => {
    if (!currentUser) {
      alert('Please log in to save favorite recipes.');
      return;
    }

    try {
      if (isFavorited(recipe)) {
        const favoriteId = favoriteIdByExternalId.get(recipe.externalId);
        await apiDelete(`/recipe-favorites/${favoriteId}`, { auth: true });
        setFavoriteIdByExternalId((prev) => {
          const next = new Map(prev);
          next.delete(recipe.externalId);
          return next;
        });
      } else {
        const created = await apiPost(
          '/recipe-favorites',
          {
            source: 'TheMealDB',
            externalId: recipe.externalId,
            title: recipe.title,
            imageUrl: recipe.imageUrl || undefined,
          },
          { auth: true },
        );
        setFavoriteIdByExternalId((prev) => new Map(prev).set(recipe.externalId, created.id));
      }
    } catch (err) {
      console.error('Error updating favorite:', err.message);
    }
  };

  const handleAddToShoppingList = async (recipe) => {
    if (!currentUser) {
      alert('Please log in to add items to your shopping list.');
      return;
    }

    try {
      const uniqueIngredients = [...new Set(recipe.ingredients)];

      const results = await Promise.allSettled(
        uniqueIngredients.map((name) => apiPost('/shopping-list', { name }, { auth: true }))
      );

      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        alert(`Added ${uniqueIngredients.length - failed}/${uniqueIngredients.length} ingredients (some failed).`);
      } else {
        alert('Ingredients added to shopping list!');
      }
    } catch (error) {
      console.error('Error adding ingredients:', error);
      alert('Failed to add ingredients to your shopping list.');
    }
  };

  const handleManualSearch = async (term, type = 'ingredient') => {
    try {
      setLoading(true);
      setError(null);
      const results = await apiGet('/recipes', { query: { ingredient: term } });
      if (results.length === 0 && type === 'ingredient') {
        const fallback = await apiGet('/recipes/category', { query: { name: term } });
        setRecipes(fallback);
      } else {
        setRecipes(results);
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      console.error('Error searching recipes:', err);
    }
  };

  if (!currentUser) {
    return (
      <div className="recipes-tab">
        <h2>Recipe Ideas</h2>
        <p className="recipes-description">
          Log in and select some deals on the Deals page to see recipe ideas.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading recipes...</div>;
  }

  if (error) {
    return (
      <div className="recipes-tab">
        <h2>Recipe Ideas</h2>
        <p className="recipes-description">
          Select deals that interest you. These will be used to generate recipe ideas.
        </p>
        <div className="error">Error loading recipes: {error}</div>
      </div>
    );
  }

  if (!hasSelections) {
    return (
      <div className="recipes-tab">
        <h2>Recipe Ideas</h2>
        <p className="recipes-description">
          You haven't selected any deals yet. Head to the Deals page to pick some.
        </p>
      </div>
    );
  }

  return (
    <div className="recipes-tab">
      <h2>Recipe Ideas</h2>
      <p className="recipes-description">
        Select deals that interest you. These will be used to generate recipe ideas.
      </p>

      {recipes.length > 0 && (
        <fieldset className="recipes-filters">
          <legend>Filter by type</legend>
          {CATEGORY_FILTERS.map((category) => (
            <label key={category} className="filter-checkbox">
              <input
                type="checkbox"
                checked={selectedCategories.has(category)}
                onChange={() => toggleCategory(category)}
              />
              {category}
            </label>
          ))}
        </fieldset>
      )}

      {visibleRecipes.length > 0 ? (
        <div className="recipes-grid">
          {visibleRecipes.map((recipe) => (
            <div key={recipe.externalId} className="recipe-card">
              {recipe.imageUrl && (
                <img src={recipe.imageUrl} alt={`${recipe.title} thumbnail`} className="recipe-image" />
              )}
              <div className="recipe-content">
                <div className="recipe-title-row">
                  <h3>{recipe.title}</h3>
                  <button
                    onClick={() => toggleFavorite(recipe)}
                    className={`favorite-toggle-btn ${isFavorited(recipe) ? 'favorited' : ''}`}
                    aria-label={isFavorited(recipe) ? 'Remove from favorites' : 'Save to favorites'}
                    aria-pressed={isFavorited(recipe)}
                  >
                    {isFavorited(recipe) ? '♥' : '♡'}
                  </button>
                </div>
                {recipe.ingredients?.length > 0 && (
                  <div className="ingredients">
                    <ul>
                      {recipe.ingredients.map((ingredient) => (
                        <li key={ingredient}>{ingredient}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button onClick={() => handleAddToShoppingList(recipe)} className="add-to-list-btn">
                  Add to Shopping List
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="no-recipes">
          {recipes.length === 0
            ? 'No recipes available for the selected deals.'
            : 'No recipes match the selected filters.'}
        </p>
      )}

      <div className="recipe-manual-search">
        <h3>Can't find what you're looking for?</h3>
        <p>Search manually by ingredient or category:</p>
        <div className="manual-search-row">
          <input
            type="text"
            value={manualSearch}
            onChange={(e) => setManualSearch(e.target.value)}
            placeholder="e.g. Spinach, Pasta, Breakfast..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && manualSearch.trim()) {
                handleManualSearch(manualSearch.trim());
              }
            }}
          />
          <button
            onClick={() => manualSearch.trim() && handleManualSearch(manualSearch.trim())}
            disabled={!manualSearch.trim() || loading}
            className="btn btn-primary"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
          <button
            onClick={() => {
              const category = BACKUP_CATEGORIES[Math.floor(Math.random() * BACKUP_CATEGORIES.length)];
              setManualSearch(category);
              handleManualSearch(category, 'category');
            }}
            disabled={loading}
            className="btn btn-secondary"
          >
            Surprise Me
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecipesTab;
